// Sidebar.tsx
import React, {useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import { Layout, Typography, Button } from 'antd';
import './Sidebar.css';
import {
    BookOutlined,
    SettingOutlined,
    UserOutlined,
    CodeOutlined,
    KeyOutlined,
    DashboardOutlined,
    CodeSandboxOutlined,
    BellOutlined,
    ProjectOutlined,
    PictureOutlined,
    BranchesOutlined,
    AppstoreOutlined,
    ProfileOutlined,
    ProductOutlined,
    TranslationOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    CreditCardOutlined,
    DollarOutlined,
    BankOutlined,
    FunnelPlotOutlined,
    LineChartOutlined,
    FundProjectionScreenOutlined,
    ShareAltOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { CODIFICATION_ROLES } from '../../constant/codification';
import { tilmidPlusURL, appVersion } from '../../config/config';
import { useTilmiAuth } from '../../hooks/useTilmiAuth';
import { useIsTablet } from '../../hooks/useIsTablet';

const palette = {
    primary: '#2992BF',
    secondary: '#8BBE1E',
    border: '#e0e7f1',
    textPrimary: '#0f172a',
    textMuted: '#6b7280',
    surface: '#ffffff',
};

const { Sider } = Layout;
const { Text } = Typography;

interface SidebarProps {
    isCollapsed: boolean;
    onToggleSidebar: () => void;
    fromToggle: boolean;
    setFromToggle: (value: boolean) => void;
}

interface MenuItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    path?: string;
    requiredRoles?: string[];
    subpages?: string[];
    onClick?: () => void;
}

interface MenuSection {
    title: string;
    items: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggleSidebar, fromToggle, setFromToggle }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { isRoleExist } = useTilmiAuth();
    const isTablet = useIsTablet();
    const chatUrl = tilmidPlusURL;

    const styles = {
        sider: {
            background: 'linear-gradient(180deg, rgba(255,255,255,0.97), rgba(244,248,251,0.94))',
            borderRight: `1px solid ${palette.border}`,
            position: 'fixed' as const,
            left: 0,
            top: 0,
            height: '100vh',
            width: isTablet && !isCollapsed ? '80vw' : (isCollapsed ? 80 : 250),
            maxWidth: isTablet && !isCollapsed ? 360 : undefined,
            minWidth: isCollapsed ? 80 : undefined,
            display: 'flex',
            flexDirection: 'column' as const,
            transition: 'width 0.25s ease',
            boxShadow: isTablet && !isCollapsed ? '20px 0 40px rgba(15,23,42,0.15)' : 'none',
            zIndex: 100,
        },
        header: {
            padding: '18px 16px 10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
        },
        headerLabel: {
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.18em',
            textTransform: 'uppercase' as const,
            color: palette.textMuted,
        },
        headerLabelCollapsed: {
            writingMode: 'vertical-rl' as const,
            transform: 'rotate(180deg)',
            fontSize: 11,
            letterSpacing: '0.2em',
            color: palette.textMuted,
        },
        scrollArea: {
            flex: 1,
            overflowY: 'auto' as const,
            overflowX: 'hidden' as const,
            padding: '12px 12px 0',
        },
        logo: {
            height: 34,
        },
        brand: {
            fontSize: 15,
            fontWeight: 600,
            color: palette.textPrimary,
            margin: 0,
        },
        toggleButton: {
            width: 32,
            height: 32,
            borderRadius: 12,
            border: `1px solid ${palette.border}`,
            background: 'linear-gradient(135deg, #ffffff, #f6fafc)',
            color: palette.textPrimary,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 6px 16px rgba(16,24,40,0.08)',
        },
        section: {
            padding: '10px 10px 0',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: 10,
        },
        sectionTitle: {
            fontSize: 11,
            letterSpacing: '0.16em',
            textTransform: 'uppercase' as const,
            color: palette.textMuted,
            paddingLeft: 6,
        },
        menuList: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: 8,
        },
        menuButton: (active: boolean, collapsed: boolean) => ({
            border: collapsed ? '1px solid transparent' : `1px solid ${active ? palette.primary + '44' : palette.border}`,
            background: collapsed
                ? 'rgba(255,255,255,0.85)'
                : active
                    ? `linear-gradient(135deg, ${palette.primary}20, ${palette.secondary}20)`
                    : '#ffffff',
            color: active ? palette.textPrimary : palette.textMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: collapsed ? 0 : 12,
            borderRadius: collapsed ? 999 : 14,
            padding: collapsed ? '10px' : '10px 12px',
            fontWeight: active ? 700 : 500,
            cursor: 'pointer',
            boxShadow: active ? '0 10px 22px rgba(41,146,191,0.18)' : '0 4px 12px rgba(15,23,42,0.04)',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease, border 0.2s ease',
        }),
        icon: {
            fontSize: 16,
            color: palette.primary,
            minWidth: 20,
            display: 'inline-flex',
            justifyContent: 'center',
        },
        version: {
            position: 'absolute' as const,
            left: 0,
            right: 0,
            bottom: 0,
            padding: '14px',
            textAlign: 'center' as const,
            color: palette.textMuted,
            fontSize: 11,
            borderTop: `1px solid ${palette.border}`,
            background: '#ffffff',
        },
    };

    // Fermer uniquement au changement de route
    useEffect(() => {
        if (isTablet && !isCollapsed) {
            if (fromToggle) {
                // On réinitialise le flag, on ne ferme pas tout de suite
                setFromToggle(false);
                return;
            }
            onToggleSidebar();
        }
        // eslint-disable-next-line
    }, [location]);

    // Définition des icônes pour les chemins spécifiques
    const getIconForPath = (path: string) => {
        switch (path) {
            case '/dashboard':
                return <DashboardOutlined />;
            case '/program':
                return <BookOutlined />;
            case '/tilmidai':
                return <img width="16" height="16" src="https://img.icons8.com/ios/50/gemini-ai.png" alt="gemini-ai" />;
            case '/conf/users':
            case '/setup/users':
                return <UserOutlined />;
            case '/setup/signup-funnel':
                return <FunnelPlotOutlined />;
            case '/setup/subject':
                return <SettingOutlined />;
            //case '/setup/blockly-objects':
            //    return <BlockOutlined />;
            case '/setup/media':
                return <PictureOutlined />;
            //case '/setup/sprites':
            //    return <CodeSandboxOutlined />;
            case '/setup/advert':
                return <BellOutlined />;
            case '/setup/roles':
                return <KeyOutlined />;
            case '/setup/translations':
                return <TranslationOutlined />;
            case '/td':
            case '/rd':
                return <CodeOutlined />;
            case '/setup/lesson_project':
                return <CodeSandboxOutlined />;
            case '/workflow':
                return <BranchesOutlined />;
            case '/student-projects':
                return <ProfileOutlined />;
            case '/projects':
                return <ProjectOutlined />;
            case '/student-projects-store':
                return <ProductOutlined />;
            case '/student-projects-tilmistore':
                return <AppstoreOutlined />;
            case '/paiement':
                return <CreditCardOutlined />;
            case '/paiement/pricing':
                return <DollarOutlined />;
            case '/paiement/pricing-b2b':
                return <BankOutlined />;
            case '/monitoring/students':
                return <FundProjectionScreenOutlined />;
            case '/monitoring/finance':
                return <LineChartOutlined />;
            case '/monitoring/ai-prices':
                return <DollarOutlined />;
            case '/apporteur':
                return <ShareAltOutlined />;
            case '/setup/apporteurs':
                return <TeamOutlined />;
            default:
                return <BookOutlined />;
        }
    };

    // Création des sections du menu
    const menuSections: MenuSection[] = [
        {
            title: t('sidebar.general'),
            items: [
                {
                    key: '/program',
                    icon: getIconForPath('/program'),
                    label: t('sidebar.program'),
                    path: '/program',
                    requiredRoles: [CODIFICATION_ROLES.ACCESS_PROGRAM],
                    subpages: ['/subject-courses/:subjectId', '/course-details/:courseId', '/course-progression/:lessonId'],
                },
                {
                    key: '/tilmidai',
                    icon: getIconForPath('/tilmidai'),
                    label: t('sidebar.tilmidPlus'),
                    onClick: () => window.open(chatUrl, '_blank'),
                    requiredRoles: [CODIFICATION_ROLES.ACCESS_TILMIDAI],
                },
                {
                    key: '/projects',
                    icon: getIconForPath('/projects'),
                    label: t('sidebar.myCreations'),
                    path: '/projects',
                    requiredRoles: [CODIFICATION_ROLES.ACCESS_PROJECT],
                    subpages: [],
                },
                {
                    key: '/dashboard',
                    icon: getIconForPath('/dashboard'),
                    label: t('sidebar.dashboard'),
                    path: '/dashboard',
                    requiredRoles: [CODIFICATION_ROLES.DASHBOARD],
                    subpages: [],
                },
                {
                    key: '/student-projects',
                    icon: getIconForPath('/student-projects'),
                    label: t('sidebar.projects'),
                    path: '/student-projects',
                    requiredRoles: [CODIFICATION_ROLES.PROJECT_LIST],
                    subpages: [],
                },
            ],
        },
        {
            title: t('sidebar.store'),
            items: [
                {
                    key: '/student-projects-store',
                    icon: getIconForPath('/student-projects-store'),
                    label: t('sidebar.store'),
                    path: '/student-projects-store',
                    requiredRoles: [CODIFICATION_ROLES.SCHOOL_STORE],
                    subpages: [],
                },
                {
                    key: '/student-projects-tilmistore',
                    icon: getIconForPath('/student-projects-tilmistore'),
                    label: t('sidebar.tilmiStore'),
                    path: '/student-projects-tilmistore',
                    requiredRoles: [CODIFICATION_ROLES.TILMICODE_STORE],
                    subpages: [],
                },
            ],
        },
        {
            title: t('sidebar.apporteur', 'Parrainage'),
            items: [
                {
                    key: '/apporteur',
                    icon: getIconForPath('/apporteur'),
                    label: t('sidebar.espaceApporteur', 'Mon espace animateur'),
                    path: '/apporteur',
                    requiredRoles: [CODIFICATION_ROLES.APPORTEUR],
                    subpages: [],
                },
            ],
        },
        {
            title: t('sidebar.monitoring', 'Monitoring'),
            items: [
                {
                    key: '/monitoring/students',
                    icon: getIconForPath('/monitoring/students'),
                    label: t('sidebar.monitoringStudents', "Suivi élève & IA"),
                    path: '/monitoring/students',
                    requiredRoles: [CODIFICATION_ROLES.MONITORING, CODIFICATION_ROLES.ADMIN_USERS],
                    subpages: [],
                },
                {
                    key: '/monitoring/finance',
                    icon: getIconForPath('/monitoring/finance'),
                    label: t('sidebar.monitoringFinance', 'Financier'),
                    path: '/monitoring/finance',
                    requiredRoles: [CODIFICATION_ROLES.MONITORING_FINANCE, CODIFICATION_ROLES.ADMIN_USERS],
                    subpages: [],
                },
                {
                    key: '/monitoring/ai-prices',
                    icon: getIconForPath('/monitoring/ai-prices'),
                    label: t('sidebar.monitoringAiPrices', 'Tarifs IA'),
                    path: '/monitoring/ai-prices',
                    requiredRoles: [CODIFICATION_ROLES.ADMIN_USERS],
                    subpages: [],
                },
            ],
        },
        {
            title: t('sidebar.settings'),
            items: [
                {
                    key: '/conf/users',
                    icon: getIconForPath('/conf/users'),
                    label: t('sidebar.users'),
                    path: '/conf/users',
                    requiredRoles: [CODIFICATION_ROLES.CONFIGURATION_OWN_USERS],
                    subpages: ['/conf/users/form'],
                },
                {
                    key: '/paiement',
                    icon: getIconForPath('/paiement'),
                    label: t('sidebar.paiement'),
                    path: '/paiement',
                    requiredRoles: [CODIFICATION_ROLES.PAIEMENT],
                    subpages: [],
                },
                {
                    key: '/paiement/pricing',
                    icon: getIconForPath('/paiement/pricing'),
                    label: t('sidebar.pricingB2C', 'Tarifs B2C'),
                    path: '/paiement/pricing',
                    requiredRoles: [CODIFICATION_ROLES.PRICING],
                    subpages: [],
                },
                {
                    key: '/paiement/pricing-b2b',
                    icon: getIconForPath('/paiement/pricing-b2b'),
                    label: t('sidebar.pricingB2B', 'Tarifs B2B'),
                    path: '/paiement/pricing-b2b',
                    requiredRoles: [CODIFICATION_ROLES.PRICING],
                    subpages: [],
                },
            ],
        },
        {
            title: t('sidebar.admin'),
            items: [
                {
                    key: '/setup/subject',
                    icon: getIconForPath('/setup/subject'),
                    label: t('sidebar.subject'),
                    path: '/setup/subject',
                    requiredRoles: [CODIFICATION_ROLES.COURSE_CONFIGURATOR],
                    subpages: ['/setup/course', '/setup/chapter', '/setup/epic', '/setup/lesson', 'lesson-blockly-phaser', '/setup/lesson-blockly-game', '/setup/lesson-blockly-draw'],
                },
                {
                    key: '/setup/lesson_project',
                    icon: getIconForPath('/setup/lesson_project'),
                    label: t('sidebar.lessonProject'),
                    path: '/setup/lesson_project',
                    requiredRoles: [CODIFICATION_ROLES.COURSE_CONFIGURATOR],
                    subpages: [],
                },
                {
                    key: '/setup/users',
                    icon: getIconForPath('/setup/users'),
                    label: t('sidebar.users'),
                    path: '/setup/users',
                    requiredRoles: ['ADMIN_USERS'],
                    subpages: ['/setup/passwords'],
                },
                {
                    key: '/setup/apporteurs',
                    icon: getIconForPath('/setup/apporteurs'),
                    label: t('sidebar.apporteursAdmin', 'Animateurs apporteurs'),
                    path: '/setup/apporteurs',
                    requiredRoles: [CODIFICATION_ROLES.ADMIN_USERS, CODIFICATION_ROLES.PRICING],
                    subpages: [],
                },
                {
                    key: '/setup/signup-funnel',
                    icon: getIconForPath('/setup/signup-funnel'),
                    label: t('sidebar.signupFunnel', "Tunnel d'inscription"),
                    path: '/setup/signup-funnel',
                    requiredRoles: ['ADMIN_USERS'],
                    subpages: [],
                },
                //{
                //    key: '/setup/blockly-objects',
                //    icon: getIconForPath('/setup/blockly-objects'),
                //    label: t('sidebar.blocklyobjects'),
                //    path: '/setup/blockly-objects',
                //    requiredRoles: [CODIFICATION_ROLES.BLOCKLY_CONFIGURATOR],
                //    subpages: [],
                //},
                {
                    key: '/setup/media',
                    icon: getIconForPath('/setup/media'),
                    label: t('sidebar.media'),
                    path: '/setup/media',
                    requiredRoles: [CODIFICATION_ROLES.BLOCKLY_CONFIGURATOR],
                    subpages: [],
                },
                {
                    key: '/workflows',
                    icon: getIconForPath('/workflow'),
                    label: t('sidebar.workflow'),
                    path: '/workflows',
                    requiredRoles: [CODIFICATION_ROLES.ADMIN_ADVERT],
                    subpages: [],
                },
                //{
                //    key: '/setup/sprites',
                //    icon: getIconForPath('/setup/sprites'),
                //    label: t('sidebar.sprites'),
                //    path: '/setup/sprites',
                //    requiredRoles: [CODIFICATION_ROLES.BLOCKLY_CONFIGURATOR],
                //    subpages: [],
                //},
                {
                    key: '/setup/roles',
                    icon: getIconForPath('/setup/roles'),
                    label: t('sidebar.roles'),
                    path: '/setup/roles',
                    requiredRoles: [CODIFICATION_ROLES.ROLE_CONFIGURATOR],
                    subpages: [],
                },
                {
                    key: '/setup/translations',
                    icon: getIconForPath('/setup/translations'),
                    label: t('sidebar.translations'),
                    path: '/setup/translations',
                    requiredRoles: [CODIFICATION_ROLES.TRANSLATION_MANAGER],
                    subpages: [],
                },
                {
                    key: '/setup/advert',
                    icon: getIconForPath('/setup/advert'),
                    label: t('sidebar.advert'),
                    path: '/setup/advert',
                    requiredRoles: [CODIFICATION_ROLES.ADMIN_ADVERT],
                    subpages: [],
                },
                {
                    key: '/rd',
                    icon: getIconForPath('/rd'),
                    label: t('sidebar.rd'),
                    path: '/rd',
                    requiredRoles: [CODIFICATION_ROLES.ADMIN_ADVERT],
                    subpages: [],
                },
            ],
        },
    ];

    // Fonction pour matcher les chemins dynamiques
    const isPathMatching = (menuPath: string, currentPath: string) => {
        return matchPath({ path: menuPath, end: false }, currentPath) !== null;
    };

    // Récupérer la clé du menu sélectionné (préférer le chemin le plus spécifique pour éviter que /paiement prenne le highlight sur /paiement/pricing)
    const getSelectedMenuKey = () => {
        const currentPath = location.pathname;
        let selectedKey = '';
        let selectedPathLength = -1;
        for (const section of menuSections) {
            for (const item of section.items) {
                const pathMatch = item.path && isPathMatching(item.path, currentPath);
                const subpageMatch = item.subpages && item.subpages.some(subpage => isPathMatching(subpage, currentPath));
                if (pathMatch || subpageMatch) {
                    const len = (item.path || '').length;
                    if (len > selectedPathLength) {
                        selectedPathLength = len;
                        selectedKey = item.key;
                    }
                }
            }
        }
        return selectedKey;
    };

    const activeKey = getSelectedMenuKey();

    const visibleSections = menuSections
        .map((section) => ({
            ...section,
            items: section.items.filter((item) =>
                item.requiredRoles ? item.requiredRoles.some((role) => isRoleExist(role)) : true
            ),
        }))
        .filter((section) => section.items.length > 0);

    const handleItemClick = (item: MenuItem) => {
        if (item.onClick) {
            item.onClick();
        } else if (item.path) {
            navigate(item.path);
        }
    };

    return (
        <>
            {isTablet && !isCollapsed && (
                <div
                    onClick={onToggleSidebar}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15,23,42,0.35)',
                        zIndex: 90,
                    }}
                />
            )}

            <Sider
                collapsed={isCollapsed}
                width={(!isCollapsed && isTablet) ? '100vw' : (isCollapsed ? 80 : 250)}
                style={styles.sider}
            >
                <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <div style={styles.header}>
                        {!isCollapsed && <Text style={styles.headerLabel}>{t('sidebar.menuLabel', 'Navigation')}</Text>}
                        <Button type="text" onClick={onToggleSidebar} style={styles.toggleButton}>
                            {isCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                        </Button>
                    </div>

                    <div className="sidebar-scroll-area" style={{
                        ...styles.scrollArea,
                        overflowY: isCollapsed ? 'hidden' : 'auto',
                        padding: isCollapsed ? '12px 8px 80px' : '12px 12px 80px'
                    }}>
                        {visibleSections.map((section) => (
                            <div key={section.title} style={styles.section}>
                                {!isCollapsed && <Text style={styles.sectionTitle}>{section.title}</Text>}
                                <div style={styles.menuList}>
                                    {section.items.map((item) => {
                                        const active = activeKey === item.key;
                                        const buttonStyle = styles.menuButton(active, isCollapsed);
                                        return (
                                            <button
                                                key={item.key}
                                                type="button"
                                                style={buttonStyle}
                                                onClick={() => handleItemClick(item)}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(4px)';
                                                    if (!isCollapsed) {
                                                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(41,146,191,0.18)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.transform = 'translateX(0)';
                                                    e.currentTarget.style.boxShadow = buttonStyle.boxShadow as string;
                                                }}
                                            >
                                                <span style={styles.icon}>{item.icon}</span>
                                                {!isCollapsed && item.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div style={styles.version}>
                        {t('sidebar.versionLabel', 'Version')} {appVersion || 'dev'}
                    </div>
                </div>
            </Sider>
        </>
    );
};

export default Sidebar;
